import React, { PropsWithChildren } from "react";
import {
  CombinedHeader,
  DefaultHeader,
  CombinedCell
} from "components/SharedTable/tableRenderers";
import { CaseActionRequestButton } from "components/ActiveIssues/CaseActionRequestButton";
import { CellProps, Column, HeaderProps, SortByFn } from "react-table";
import { ActiveIssuesCaseData } from "./ActiveIssuesInterfaces";
import { CaseStatusCategory } from "interfaces/case-status-type";
import { DotMeter } from "components/DotMeter";
import { Link } from "react-router-dom";
import { Link as MuiLink, Tooltip } from "@mui/material";
import { caseIdToDisplayId } from "core/utils/str";
import { styled } from "@mui/material/styles";

// The number of dots that should appear on the case status category meter
const CASE_CATEGORY_METER_SIZE = 5;

const mapStatusCategoryToDotsFilled = (caseStatusCategory: string): number => {
  switch (caseStatusCategory) {
    case CaseStatusCategory.ALARM:
      return 0;
    case CaseStatusCategory.NEW:
      return 1;
    case CaseStatusCategory.INSPECTION:
      return 2;
    case CaseStatusCategory.WAITING:
      return 3;
    case CaseStatusCategory.REMEDIATION:
      return 4;
    case CaseStatusCategory.COMPLETE:
      return 5;
    default:
      console.error("Unknown case status category");
      return 0;
  }
};

// Custom Sorting function for the Site/Machine column.
// localeCompare() method returns a number indicating whether a reference string comes before, or after, or is the same as the given string in sort order.
const sortSiteMachine: SortByFn<ActiveIssuesCaseData> = (rowA, rowB) => {
  const siteComparison = rowA.values.site_machine[0].localeCompare(
    rowB.values.site_machine[0]
  );
  if (siteComparison !== 0) {
    return siteComparison;
  }
  return rowA.values.site_machine[1].localeCompare(rowB.values.site_machine[1]);
};

export const COLUMNS: Column<ActiveIssuesCaseData>[] = [
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Case ID"
      }),
    accessor: "case_id" as const,
    disableFilters: true,
    Cell: (data: PropsWithChildren<HeaderProps<ActiveIssuesCaseData>>) => (
      <MuiLink
        component={Link}
        onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) =>
          event.stopPropagation()
        }
        to={`/pages/case-management/${data.cell.row.original.case_id}`}
      >
        {caseIdToDisplayId(data.cell.value)}
      </MuiLink>
    )
  },
  {
    Header: data =>
      CombinedHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Site",
        subHeader: "Machine"
      }),
    id: "site_machine",
    accessor: data => [data.site_name, data.machine_name],
    sortType: sortSiteMachine,
    Cell: (data: PropsWithChildren<HeaderProps<ActiveIssuesCaseData>>) =>
      CombinedCell({ value: data.cell.value })
  },
  {
    Header: data =>
      CombinedHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Losses To Date",
        subHeader: "(MWh)"
      }),
    accessor: "losses_to_date_mwh" as const,
    disableFilters: true,
    Cell: ({ value }: { value: number }) => (
      <TruncatedCell value={value.toFixed(2)} />
    )
  },
  {
    Header: data =>
      CombinedHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Predicted Energy Loss",
        subHeader: "(MWh) (1 Month)"
      }),
    accessor: "predicted_energy_loss_next_month_mwh" as const,
    disableFilters: true,
    Cell: (cellProps: CellProps<ActiveIssuesCaseData>) => (
      <TruncatedCell value={cellProps.value} />
    )
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,

        header: "Case Status"
      }),
    id: "case_status",
    accessor: row => mapStatusCategoryToDotsFilled(row.case_status_category),
    filter: "select",
    Cell: ({
      value,
      row
    }: {
      value: number;
      row: { original: { case_status_name: string } };
    }) => {
      return (
        <DotWrapper>
          <DotMeter
            size={CASE_CATEGORY_METER_SIZE}
            filled={value}
            title={row.original.case_status_name}
          />
        </DotWrapper>
      );
    }
  },
  {
    Header: data =>
      CombinedHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Updated",
        subHeader: "(Days Ago)"
      }),
    accessor: "days_since_last_updated" as const,
    disableFilters: true,
    Cell: (cellProps: CellProps<ActiveIssuesCaseData>) => (
      <TruncatedCell value={cellProps.value} />
    )
  },
  {
    Header: "Request Update",
    disableFilters: true,
    Cell: (data: PropsWithChildren<HeaderProps<ActiveIssuesCaseData>>) => (
      <CaseActionRequestButton
        caseId={data?.cell?.row?.original?.case_id}
        assignedUser={{
          id: data?.cell?.row?.original?.assignee_id,
          name: data?.cell?.row?.original?.assignee_name
        }}
      />
    )
  }
];

const DotWrapper = styled("div")`
  display: flex;
  justify-content: flex-start;
`;

export const TruncatedCell = ({
  value
}: {
  value?: string | number | undefined;
}): JSX.Element => (
  <TruncatedCellWrapper>
    <Tooltip title={value ?? ""} disableHoverListener={!value}>
      <ValueWrapper>{value ?? "-"}</ValueWrapper>
    </Tooltip>
  </TruncatedCellWrapper>
);

const TruncatedCellWrapper = styled("div")`
  display: flex;
  justify-content: flex-start;
`;

const ValueWrapper = styled("div")`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100px;
`;
